import React from "react";
import { fmtDate } from "@/lib/format";

/** Compact policy/Antrag details preview used in the Policen and Anträge lists.
 *  Renders rich type-specific summary when row is expanded.
 */
export default function PolicyDetailsPreview({ details, type, t }) {
  if (!details) return null;
  // Malattia: vertical layout — KVG row, VVG row, Modello medico row, Familiari list
  if (type === "malattia") {
    const fam = details.family_members || [];
    return (
      <div className="space-y-1 mt-1.5 text-xs">
        {(details.kvg_start || details.kvg_end) && (
          <div className="flex items-baseline gap-2 font-mono">
            <span className="font-bold text-black min-w-[36px]">KVG</span>
            <span className="font-semibold text-green-700">{fmtDate(details.kvg_start)}</span>
            <span className="text-muted-foreground">→</span>
            <span className="font-semibold text-red-700">{fmtDate(details.kvg_end)}</span>
          </div>
        )}
        {(details.vvg_start || details.vvg_end) && (
          <div className="flex items-baseline gap-2 font-mono">
            <span className="font-bold text-black min-w-[36px]">VVG</span>
            <span className="font-semibold text-green-700">{fmtDate(details.vvg_start)}</span>
            <span className="text-muted-foreground">→</span>
            <span className="font-semibold text-red-700">{fmtDate(details.vvg_end)}</span>
          </div>
        )}
        {details.medical_model && (
          <div className="flex items-baseline gap-2">
            <span className="font-bold text-black min-w-[110px]">Modello medico</span>
            <span className="text-foreground">{details.medical_model}</span>
          </div>
        )}
        {fam.length > 0 && (
          <div className="mt-1 pt-1 border-t border-dashed border-border">
            <div className="font-bold text-black mb-1">Familiari ({fam.length})</div>
            <div className="space-y-1 pl-2 border-l-2 border-prime/30">
              {fam.map((m, i) => (
                <div key={i} className="font-mono text-[11px]" data-testid={`fam-preview-${i}`}>
                  <span className="font-semibold text-foreground">
                    {(m.first_name || "") + " " + (m.last_name || "")}
                  </span>
                  {(m.kvg_start || m.kvg_end) && (
                    <span className="ml-2">
                      <span className="font-bold text-black">KVG</span>{" "}
                      <span className="text-green-700 font-semibold">{fmtDate(m.kvg_start)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="text-red-700 font-semibold">{fmtDate(m.kvg_end)}</span>
                    </span>
                  )}
                  {(m.vvg_start || m.vvg_end) && (
                    <span className="ml-2">
                      <span className="font-bold text-black">VVG</span>{" "}
                      <span className="text-green-700 font-semibold">{fmtDate(m.vvg_start)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="text-red-700 font-semibold">{fmtDate(m.vvg_end)}</span>
                    </span>
                  )}
                  {m.doctor && (
                    <span className="ml-2 text-muted-foreground">· medico: {m.doctor}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vita 3A/3B
  if (type === "vita_3a" || type === "vita_3b") {
    const benefs = Array.isArray(details.beneficiaries) ? details.beneficiaries : [];
    return (
      <div className="mt-1.5 text-xs space-y-1">
        {details.is_kinder_lv && (
          <div className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 bg-pink-100 text-pink-700 rounded font-bold">Kinder-LV</div>
        )}
        {(details.insured_first_name || details.insured_last_name) && (
          <div><span className="font-bold text-black">Versicherte Person:</span> {details.insured_first_name} {details.insured_last_name} {details.insured_dob ? `(${fmtDate(details.insured_dob)})` : ""}</div>
        )}
        {details.annual_premium && <div><span className="font-bold text-black">Jahresprämie:</span> CHF {Number(details.annual_premium).toLocaleString("de-CH")}</div>}
        {details.annual_contribution && <div><span className="font-bold text-black">Jahresbeitrag:</span> CHF {Number(details.annual_contribution).toLocaleString("de-CH")}</div>}
        {details.contribution_type && <div><span className="font-bold text-black">Beitragsart:</span> {details.contribution_type}</div>}
        {details.contract_type && <div><span className="font-bold text-black">Vertragsart:</span> {details.contract_type}</div>}
        {details.guaranteed_rate && <div><span className="font-bold text-black">Garantierter Zins:</span> {details.guaranteed_rate}%</div>}
        {details.retirement_age && <div><span className="font-bold text-black">Pensionierungsalter:</span> {details.retirement_age}</div>}
        {details.maturity_capital && <div><span className="font-bold text-black">Kapital bei Ablauf:</span> CHF {Number(details.maturity_capital).toLocaleString("de-CH")}</div>}
        {details.death_sum && <div><span className="font-bold text-black">Todesfallsumme:</span> CHF {Number(details.death_sum).toLocaleString("de-CH")}</div>}
        {benefs.length > 0 && (
          <div className="mt-1 pt-1 border-t border-dashed border-border">
            <div className="font-bold text-black mb-1">Begünstigte ({benefs.length})</div>
            <div className="pl-2 border-l-2 border-prime/30 space-y-0.5">
              {benefs.map((b, i) => (
                <div key={i} className="font-mono text-[11px]">
                  {b.first_name} {b.last_name} <span className="text-muted-foreground">· {b.relationship}</span> {b.share_pct ? <span className="font-semibold">{b.share_pct}%</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Hypothek
  if (type === "hypothek") {
    const tranches = Array.isArray(details.tranches) ? details.tranches : [];
    return (
      <div className="mt-1.5 text-xs space-y-1">
        {details.property_address && <div><span className="font-bold text-black">Liegenschaft:</span> {details.property_address}</div>}
        {details.property_type && <div><span className="font-bold text-black">Objekttyp:</span> {details.property_type}</div>}
        {details.market_value && <div><span className="font-bold text-black">Marktwert:</span> CHF {Number(details.market_value).toLocaleString("de-CH")}</div>}
        {details.total_debt && <div><span className="font-bold text-black">Schuld total:</span> CHF {Number(details.total_debt).toLocaleString("de-CH")}</div>}
        {details.loan_to_value && <div><span className="font-bold text-black">Belehnung:</span> {details.loan_to_value}%</div>}
        {tranches.length > 0 && (
          <div className="mt-1 pt-1 border-t border-dashed border-border">
            <div className="font-bold text-black mb-1">Tranchen ({tranches.length})</div>
            <div className="pl-2 border-l-2 border-prime/30 space-y-0.5">
              {tranches.map((tr, i) => (
                <div key={i} className="font-mono text-[11px]">
                  <span className="font-bold">{tr.rank}</span> · {tr.type} · CHF {Number(tr.amount || 0).toLocaleString("de-CH")}
                  {tr.rate_pct && <span> · {tr.rate_pct}%</span>}
                  {tr.end_date && <span className="text-red-700"> · bis {fmtDate(tr.end_date)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Mietkaution
  if (type === "mietkaution") {
    return (
      <div className="mt-1.5 text-xs space-y-1">
        {details.property_address && <div><span className="font-bold text-black">Mietobjekt:</span> {details.property_address}</div>}
        {details.living_area && <div><span className="font-bold text-black">Wohnfläche:</span> {details.living_area} m²</div>}
        {details.monthly_rent && <div><span className="font-bold text-black">Mietzins:</span> CHF {Number(details.monthly_rent).toLocaleString("de-CH")}/Monat</div>}
        {details.deposit_amount && <div><span className="font-bold text-black">Kaution:</span> CHF {Number(details.deposit_amount).toLocaleString("de-CH")}</div>}
        {details.monthly_premium && <div><span className="font-bold text-black">Monatsprämie:</span> CHF {Number(details.monthly_premium).toLocaleString("de-CH")}</div>}
        {details.landlord_name && <div><span className="font-bold text-black">Vermieter:</span> {details.landlord_name}</div>}
      </div>
    );
  }

  // Generic chips for other types
  const chips = [];
  if (type === "auto") {
    if (details.vehicle?.make || details.vehicle?.model) chips.push(`${details.vehicle?.make || ""} ${details.vehicle?.model || ""}`.trim());
    if (details.vehicle?.plate) chips.push(`Targa ${details.vehicle.plate}`);
    if (details.coverages?.length) chips.push(`${details.coverages.length} coperture`);
  } else if (type === "casa") {
    if (details.tenure) chips.push(details.tenure === "affitto" ? "Affitto" : "Privata");
    if (details.coverages?.length) chips.push(`${details.coverages.length} coperture`);
  } else if (type === "rc") {
    if (details.scope) chips.push(details.scope === "familiare" ? "Familienmitglied" : "Personale");
    if (details.coverages?.length) chips.push(`${details.coverages.length} coperture`);
  } else if (details.coverages?.length) {
    chips.push(`${details.coverages.length} coperture`);
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {chips.map((c, i) => (
        <span key={i} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-prime/10 text-prime rounded">{c}</span>
      ))}
    </div>
  );
}
